import { useState } from 'react'
import type { BoardElement, Frame, MediaDto, Translator } from '@praesto/shared'
import { useText } from '../i18n.js'
import { isSelected, selectOne, toggleSelection, type Selection } from '../canvas/selection.js'

interface Props {
  elements: BoardElement[]
  frames: Frame[]
  mediaById: Map<string, MediaDto>
  selection: Selection
  onSelect: (selection: Selection) => void
  onMoveLayer: (id: string, delta: number) => void
  /** Verschiebt `id` direkt vor `beforeId` in der Stapelreihenfolge. */
  onReorder: (id: string, beforeId: string) => void
  onRemove: (selection: NonNullable<Selection>) => void
  onAssignToFrame: (elementId: string, frameId: string) => void
}

const ICONS: Record<BoardElement['type'], string> = {
  image: '▣',
  video: '▶',
  iframe: '◱',
  text: 'T',
}

function labelOf(
  element: BoardElement,
  mediaById: Map<string, MediaDto>,
  t: Translator,
): string {
  if (element.type === 'text') return element.text.trim().slice(0, 32) || t('layers.text')
  if (element.type === 'iframe') {
    try {
      return new URL(element.url).host
    } catch {
      return element.url
    }
  }
  return mediaById.get(element.mediaId)?.originalName ?? t('layers.media')
}

/** Liegt das Element vollständig im Frame? Dieselbe Regel wie beim Verschieben. */
const contains = (frame: Frame, element: BoardElement): boolean =>
  element.x >= frame.x - 0.5 &&
  element.y >= frame.y - 0.5 &&
  element.x + element.w <= frame.x + frame.w + 0.5 &&
  element.y + element.h <= frame.y + frame.h + 0.5

/**
 * Ebenenliste.
 *
 * Der verlässliche Weg zu jedem Objekt — auf dem Board kann ein Element unter
 * einem Frame oder hinter einem anderen Element liegen, hier ist alles
 * erreichbar. Die Gruppierung folgt derselben Geometrie-Regel wie das
 * Mitbewegen: Was vollständig im Frame liegt, gehört zu ihm.
 *
 * Ein Element auf eine Frame-Überschrift zu ziehen ordnet es dort ein — indem
 * es tatsächlich hineinbewegt wird. Es gibt keine unsichtbare zweite Wahrheit
 * neben der Position.
 */
export function LayersPanel({
  elements,
  frames,
  mediaById,
  selection,
  onSelect,
  onMoveLayer,
  onReorder,
  onRemove,
  onAssignToFrame,
}: Props): React.ReactElement {
  const t = useText()
  const [open, setOpen] = useState(true)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const [dropBefore, setDropBefore] = useState<string | null>(null)

  // Vorderstes zuerst — so, wie man Ebenen liest.
  const stacked = [...elements].reverse()
  const assigned = new Map<string, BoardElement[]>()
  const loose: BoardElement[] = []

  for (const element of stacked) {
    // Bei verschachtelten Frames gewinnt der kleinste — er ist der speziellere.
    const owner = frames
      .filter((frame) => contains(frame, element))
      .sort((a, b) => a.w * a.h - b.w * b.h)[0]
    if (owner) {
      const list = assigned.get(owner.id) ?? []
      list.push(element)
      assigned.set(owner.id, list)
    } else {
      loose.push(element)
    }
  }

  const row = (element: BoardElement, index: number, total: number): React.ReactElement => (
    <li
      key={element.id}
      className={[
        isSelected(selection, 'element', element.id) ? 'is-active' : '',
        dropBefore === element.id ? 'is-drop-before' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData('text/praesto-element', element.id)
        event.dataTransfer.effectAllowed = 'move'
      }}
      onDragEnd={() => {
        setDropBefore(null)
        setDropTarget(null)
      }}
      onDragOver={(event) => {
        if (!event.dataTransfer.types.includes('text/praesto-element')) return
        // Auf einem Eintrag heißt: hier einsortieren, nicht dem Frame zuordnen.
        event.preventDefault()
        event.stopPropagation()
        setDropBefore(element.id)
        setDropTarget(null)
      }}
      onDragLeave={() => setDropBefore((current) => (current === element.id ? null : current))}
      onDrop={(event) => {
        event.preventDefault()
        event.stopPropagation()
        setDropBefore(null)
        const dragged = event.dataTransfer.getData('text/praesto-element')
        if (dragged && dragged !== element.id) onReorder(dragged, element.id)
      }}
    >
      <span className="layer-icon">{ICONS[element.type]}</span>
      <button
        className="layer-name"
        onClick={(event) =>
          onSelect(
            // Umschalt- oder Strg-Klick nimmt hinzu; genau wie auf dem Board.
            event.shiftKey || event.metaKey || event.ctrlKey
              ? toggleSelection(selection, 'element', element.id)
              : selectOne('element', element.id),
          )
        }
      >
        {labelOf(element, mediaById, t)}
      </button>
      <span className="layer-actions">
        <button
          className="button ghost icon"
          onClick={() => onMoveLayer(element.id, 1)}
          disabled={index === 0}
          aria-label={t('layers.forward')}
        >
          ↑
        </button>
        <button
          className="button ghost icon"
          onClick={() => onMoveLayer(element.id, -1)}
          disabled={index === total - 1}
          aria-label={t('layers.backward')}
        >
          ↓
        </button>
        <button
          className="button ghost icon danger"
          onClick={() => onRemove({ kind: 'element', ids: [element.id] })}
          aria-label={t('common.delete')}
        >
          ×
        </button>
      </span>
    </li>
  )

  return (
    <aside className={`layers-panel${open ? '' : ' is-collapsed'}`}>
      <header className="layers-head">
        <button className="button ghost frames-toggle" onClick={() => setOpen((v) => !v)}>
          <span className={`chevron${open ? ' is-open' : ''}`}>›</span>
          Ebenen
          {elements.length > 0 && <span className="frames-count">{elements.length}</span>}
        </button>
      </header>

      {open && (
        <>
          {elements.length === 0 && (
            <p className="frames-empty">{t('layers.empty')}</p>
          )}

          {frames.map((frame, position) => {
            const children = assigned.get(frame.id) ?? []
            return (
              <section
                key={frame.id}
                className={`layer-group${dropTarget === frame.id ? ' is-drop' : ''}`}
                onDragOver={(event) => {
                  if (!event.dataTransfer.types.includes('text/praesto-element')) return
                  event.preventDefault()
                  setDropTarget(frame.id)
                }}
                onDragLeave={() => setDropTarget((t) => (t === frame.id ? null : t))}
                onDrop={(event) => {
                  event.preventDefault()
                  setDropTarget(null)
                  const id = event.dataTransfer.getData('text/praesto-element')
                  if (id) onAssignToFrame(id, frame.id)
                }}
              >
                <button
                  className={`layer-group-head${
                    isSelected(selection, 'frame', frame.id) ? ' is-active' : ''
                  }`}
                  onClick={(event) =>
                    onSelect(
                      event.shiftKey || event.metaKey || event.ctrlKey
                        ? toggleSelection(selection, 'frame', frame.id)
                        : selectOne('frame', frame.id),
                    )
                  }
                >
                  <span className="frames-order">{position + 1}</span>
                  {frame.name}
                  <span className="layer-group-count">{children.length}</span>
                </button>
                {children.length > 0 && (
                  <ul className="layers-list">
                    {children.map((element, index) => row(element, index, children.length))}
                  </ul>
                )}
              </section>
            )
          })}

          {loose.length > 0 && (
            <section className="layer-group">
              <span className="layer-group-head is-static">{t('layers.noFrame')}</span>
              <ul className="layers-list">
                {loose.map((element, index) => row(element, index, loose.length))}
              </ul>
            </section>
          )}
        </>
      )}
    </aside>
  )
}
